import React, { useState, useEffect } from 'react';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';
import { getInactiveTasks, deleteTask } from '../../../api/retailPerformanceTasks';
import { useAuth } from '../../../contexts/AuthContext';
import PerformanceTaskCard from '../../../components/retail/performance-tasks/PerformanceTaskCard';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const InactiveTasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    search: '',
    task_type: '',
    sort_by: 'completion_time',
    sort_order: 'DESC',
    page: 1,
    limit: 12
  });

  const statusTabs = [
    { key: 'all', label: 'All Inactive' },
    { key: 'completed', label: 'Completed' },
    { key: 'failed', label: 'Failed' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'ended', label: 'Ended Early' }
  ];

  useEffect(() => {
    fetchTasks();
  }, [activeTab, filters.page, filters.task_type, filters.sort_by, filters.sort_order]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTasks();
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const queryFilters = {
        ...filters,
        status: activeTab === 'all' ? undefined : activeTab
      };
      const response = await getInactiveTasks(queryFilters);
      setTasks(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Error fetching inactive tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Are you sure you want to permanently delete task "${task.title}"?`)) {
      return;
    }

    try {
      await deleteTask(task.id);
      toast.success('Task deleted successfully');
      fetchTasks(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting task');
    }
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const isAdmin = ['retail_admin', 'system_admin', 'super_admin', 'admin'].includes(user?.role);

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Performance History</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            View completed, failed, cancelled, and ended tasks
          </p>
        </div>
        <button
          onClick={fetchTasks}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-1">
        {statusTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              setFilters((prev) => ({ ...prev, page: 1 }));
            }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab.key
                ? 'bg-white dark:bg-gray-800 text-accent-gold border-b-2 border-accent-gold'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
          />
        </div>

        <select
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          value={filters.task_type}
          onChange={(e) => setFilters((prev) => ({ ...prev, task_type: e.target.value, page: 1 }))}
        >
          <option value="">All Types</option>
          <option value="sales_count">Sales Count</option>
          <option value="sales_amount">Sales Amount</option>
          <option value="tiles_quantity">Tiles Quantity</option>
          <option value="accessories_quantity">Accessories Quantity</option>
        </select>

        <select
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          value={filters.sort_order}
          onChange={(e) => setFilters((prev) => ({ ...prev, sort_order: e.target.value, page: 1 }))}
        >
          <option value="DESC">Newest First</option>
          <option value="ASC">Oldest First</option>
        </select>
      </div>

      {/* Content */}
      {loading && tasks.length === 0 ? (
        <LoadingSpinner message="Loading performance history..." className="py-12" />
      ) : tasks.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            No inactive tasks found matching your criteria
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {tasks.map((task) => (
              <PerformanceTaskCard
                key={task.id}
                task={task}
                showActions={isAdmin}
                onDelete={isAdmin ? handleDelete : undefined}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-center items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              <span className="text-gray-600 dark:text-gray-400">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InactiveTasksPage;
